import React, { useEffect, useState } from 'react';
import 'styles/home.css';
import 'styles/articles.css';

import { SiteFooter, SiteNav } from 'views/SiteChrome.js';
import { locationForRoute } from 'files/routes.js';
import { articleDate, featuredArticle, publishedArticles, useArticles } from 'files/articles.js';
import { ArticlesSkeleton, usePlaceholder } from 'views/Loading.js';
import { thoughtLine } from 'files/rings.js';

/* The article index.

   Topics sit in a bar across the top, not in a side rail. As a rail they took a
   fifth of the width and pushed the titles and summaries — the only thing anyone
   opens this page for — into a narrow column on the far right.

   Each entry is one column: title, summary, then the label, date and length
   underneath. The meta belongs with the piece it describes, not in a column of
   its own off to one side.

   Which piece leads is a flag on the article, not a rule in here — see
   `featured` in files/articles.js. It sorts to the top and is set larger in
   place, rather than repeated in a hero band above the list it already heads. */
// generated once at module load, not per render — the shape must never change
const DRAWING = thoughtLine();

const Articles = ({ utils }) => {

    /* The featured piece is the hero above the filter, so it is held out of the
       list, the topics and their counts — otherwise the page shows it twice and
       the counts disagree with what is under them. */
    const library = useArticles();
    const featured = featuredArticle(library.articles);
    const articles = publishedArticles(library.articles).filter(article => article !== featured);
    const [topic, setTopic] = useState(null);

    // same beat the article page holds, for the same reason — see Loading.js
    const waiting = usePlaceholder(library.loading);

    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => document.body.classList.remove('dl-body');
    }, []);

    const onOpen = slug => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'article', slug: slug });
    }

    const onQuickLook = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'quick-look' });
    }

    // topics in the order they first appear, each with its count
    const topics = articles.reduce((carry, article) => {
        const match = carry.find(item => item.name === article.topic);
        if(match) {
            match.count = match.count + 1;
            return carry;
        }
        return [...carry, { name: article.topic, count: 1 }];
    }, []);

    const shown = topic ? articles.filter(article => article.topic === topic) : articles;

    return (
        <div className={'dl'}>
            <SiteNav utils={utils} />

            {/* The hero holds its space while the list is being fetched, because
                it sits above everything else on the page: letting it appear
                afterwards would push the whole index down at the moment the
                reader had started looking at it. The drawing is generated at
                module load and owes nothing to the network, so it renders for
                real either way and the band is never empty. */}
            {(waiting || featured) && (
                <section className={'ar-hero'}>
                    <div className={'dl-wrap ar-hero-grid'}>
                    {waiting ? (
                    <div className={'sk'}>
                        <span className={'sk-line sk-meta'} />
                        <span className={'sk-line sk-title'} />
                        <span className={'sk-line sk-title is-part'} />
                        <span className={'sk-line sk-dek'} />
                        <span className={'sk-line sk-entry-meta'} />
                    </div>
                    ) : (
                    <div className={'ar-hero-say ar-arrived'}>
                        {/* Meta above the title, the way the article pages set
                            theirs, and the way in directly under the dek. The
                            foot row this replaced pushed a link to the right edge
                            of the text column, which on a wide screen is the
                            middle of the band — the one place nothing should
                            land. Everything here reads down one left edge. */}
                        <div className={'ar-hero-meta'}>
                            <span className={'ar-hero-flag'}>Featured</span>
                            <span className={'ar-entry-topic'}>{featured.topic}</span>
                            <span>{articleDate(featured.published)}</span>
                            <span>{featured.minutes} min read</span>
                        </div>
                        <a href={`/articles/${featured.slug}`} onClick={onOpen(featured.slug)}>
                            <h1 className={'dl-display'}>{featured.title}</h1>
                        </a>
                        <p className={'ar-hero-dek'}>{featured.dek}</p>
                        <a className={'dl-read ar-hero-more'} href={`/articles/${featured.slug}`} onClick={onOpen(featured.slug)}>
                            Read it <span className={'dl-arrow'}>→</span>
                        </a>
                    </div>
                    )}
                    {/* one leaf in accent, the way one ring and one form carry it
                        on the other heroes — near the middle of the pile rather
                        than at an edge, so the eye lands inside the drawing */}
                    <figure aria-hidden={'true'} className={'ar-hero-figure'}>
                        <svg fill={'none'} viewBox={'44 74 330 214'} xmlns={'http://www.w3.org/2000/svg'}>
                            <path
                            d={DRAWING.line}
                            stroke={'var(--sketch)'}
                            strokeLinecap={'round'}
                            strokeOpacity={0.5}
                            style={{ animationDelay: '-12s' }} />
                            <path
                            className={'is-sketch'}
                            d={DRAWING.mark}
                            stroke={'var(--sketch)'}
                            strokeOpacity={0.9}
                            style={{ animationDelay: '-38s' }} />
                        </svg>
                    </figure>
                    </div>
                </section>
            )}

            <section className={'ar-index'}>
                <div className={'dl-wrap'}>

                    {/* An empty list and a list that has not arrived look the
                        same on screen and mean opposite things, so neither is
                        allowed to render as the other. */}
                    {waiting && <ArticlesSkeleton />}

                    {!waiting && library.failed && (
                        <div className={'ar-state'}>
                            <h2>We could not load the list.</h2>
                            <p>Refreshing usually sorts it out. If it does not, email hello@duralabs.io and we will send you what you were after.</p>
                        </div>
                    )}

                    {!waiting && !library.failed && (
                    <>
                    {/* The same filter, twice, because the two widths want
                        genuinely different controls rather than one control
                        squeezed. A row of pills is scannable when the row fits;
                        at three hundred and ninety pixels the same pills wrap
                        into three lines of furniture above the thing being
                        filtered. A select is one line, is the control a phone
                        already knows how to present, and opens as a native
                        picker rather than as more page.

                        Rendered both ways and hidden by stylesheet rather than
                        switched on a measured width: a resize listener to choose
                        between two controls is a listener, a re-render and a
                        flash of the wrong one on first paint. */}
                    <div className={'ar-topics-select'}>
                        <label htmlFor={'ar-topic'}>Filter</label>
                        <select
                        id={'ar-topic'}
                        onChange={evt => setTopic(evt.target.value || null)}
                        value={topic || ''}>
                            <option value={''}>Everything ({articles.length})</option>
                            {topics.map(item => (
                                <option key={item.name} value={item.name}>
                                    {item.name} ({item.count})
                                </option>
                            ))}
                        </select>
                    </div>

                    <nav className={'ar-topics'} aria-label={'Filter by topic'}>
                        <button
                        className={topic === null ? 'is-on' : ''}
                        onClick={() => setTopic(null)}
                        type={'button'}>
                            Everything <span>{articles.length}</span>
                        </button>
                        {topics.map(item => (
                            <button
                            className={topic === item.name ? 'is-on' : ''}
                            key={item.name}
                            onClick={() => setTopic(item.name)}
                            type={'button'}>
                                {item.name} <span>{item.count}</span>
                            </button>
                        ))}
                    </nav>

                    <div className={'ar-list ar-arrived'}>
                        {shown.map((article, index) => (
                            <article className={'ar-entry'} key={article.slug}>
                                <a href={`/articles/${article.slug}`} onClick={onOpen(article.slug)}>
                                    <h2>{article.title}</h2>
                                </a>
                                <p className={'ar-entry-dek'}>{article.dek}</p>
                                <div className={'ar-entry-meta'}>
                                    <span className={'ar-entry-topic'}>{article.topic}</span>
                                    <span>{articleDate(article.published)}</span>
                                    <span>{article.minutes} min read</span>
                                </div>
                            </article>
                        ))}
                    </div>
                    </>
                    )}
                </div>
            </section>

            <section className={'ar-cta'}>
                <div className={'dl-wrap'}>
                    <h2 className={'dl-display'}>Not sure which of these is <em>your</em> problem?</h2>
                    <p>
                        The Quick Look asks a few pointed questions about how your business
                        runs and tells you where you are most likely leaking time and money.
                        About three minutes, no email required.
                    </p>
                    <a className={'dl-btn'} href={locationForRoute({ view: 'quick-look' })} onClick={onQuickLook}>
                        Take a Quick Look <span className={'dl-arrow'}>→</span>
                    </a>
                </div>
            </section>

            <SiteFooter utils={utils} />
        </div>
    )
}

export default Articles;
